/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

Coded by FX Bullsheet
=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import { savePreferences } from '../../preferences';
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import { MenuItem, Select, FormControl, InputLabel, Button } from "@mui/material";
import MDBox from "components/MDBox";
import TradeAccountForm from "components/TradeAccountForm";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import HomeIcon from '@mui/icons-material/Home';

function DashboardNavbar({ absolute, light, isMini, tradeAccounts, selectedAccountID, setSelectedAccountID }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);

  useEffect(() => {
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleAccountChange = async (event) => {
    console.log('handleAccountChange triggered, new value:', event.target.value);
    const newAccountID = event.target.value;
    console.log('Current selectedAccountID:', selectedAccountID);
    console.log('setSelectedAccountID function:', setSelectedAccountID);
    try {
      setSelectedAccountID(newAccountID);
      console.log('Updated selectedAccountID to:', newAccountID);
      if (localStorage.getItem('userID')) {
        await savePreferences(localStorage.getItem('userID'), {
          preferredAccountID: newAccountID,
        });
        console.log('Saved preferredAccountID to preferences:', newAccountID);
      } else {
        console.log('No userID found in localStorage');
      }
    } catch (error) {
      console.error('Error in handleAccountChange:', error);
    }
  };
  

  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <Link to="/fx-dashboard/profile" style={{ textDecoration: 'none', color: 'inherit' }}>
        <NotificationItem icon={<NotificationsIcon />} title="Check new messages" />
      </Link>
    </Menu>
  );

  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });
  console.log('Navbar state:', { tradeAccounts, selectedAccountID, disabled: !tradeAccounts || tradeAccounts.length === 0 });
  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon={<HomeIcon />} title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            {/* Trade Account Selector and Add Button */}
            <MDBox display="flex" alignItems="center" mr={2}>
  <FormControl sx={{ minWidth: 150 }}>
    <InputLabel>Trade Account</InputLabel>
    <Select
      value={selectedAccountID || ''}
      onChange={handleAccountChange}
      label="Trade Account"
      disabled={!tradeAccounts || tradeAccounts.length === 0}
    >
      {tradeAccounts && tradeAccounts.length > 0 ? (
        tradeAccounts.map((account) => (
          <MenuItem key={account.id} value={account.id}>
            {account.name}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>No accounts available</MenuItem>
      )}
    </Select>
  </FormControl>
  <Tooltip title="Add New Trade Account">
    <IconButton
      onClick={() => setShowAccountForm(true)}
      disabled={tradeAccounts && tradeAccounts.length >= 5}
      sx={{
        ml: 1,
        bgcolor: (theme) => theme.palette.grey[300], // Light grey for light mode
        color: (theme) => theme.palette.grey[900], // Dark grey for contrast
        '&:hover': { bgcolor: (theme) => theme.palette.grey[400] },
        '&.Mui-disabled': { bgcolor: (theme) => theme.palette.grey[200], color: (theme) => theme.palette.grey[500] },
        borderRadius: '50%',
        width: 24,
        height: 24,
        ...(darkMode && { // Adjust for dark mode
          bgcolor: (theme) => theme.palette.grey[700],
          color: (theme) => theme.palette.grey[100],
          '&:hover': { bgcolor: (theme) => theme.palette.grey[600] },
          '&.Mui-disabled': { bgcolor: (theme) => theme.palette.grey[800], color: (theme) => theme.palette.grey[600] },
        }),
      }}
    >
      <Icon fontSize="small">add</Icon>
    </IconButton>
  </Tooltip>
</MDBox>

            <MDBox color={light ? "white" : "inherit"}>
              <Tooltip title="Return to FX Bullsheet Website">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={() => window.location.href = "http://www.fxbullsheet.com"}
                >
                  <Icon sx={iconsStyle}><HomeIcon /></Icon>
                </IconButton>
              </Tooltip>

              <Tooltip title="Account / Profile Management">
                <Link to="/fx-dashboard/profile">
                  <IconButton sx={navbarIconButton} size="small" disableRipple>
                    <Icon sx={iconsStyle}><AccountCircleIcon /></Icon>
                  </IconButton>
                </Link>
              </Tooltip>

              <Tooltip title="Configurator">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleConfiguratorOpen}
                >
                  <Icon sx={iconsStyle}><SettingsIcon /></Icon>
                </IconButton>
              </Tooltip>

              <Tooltip title="See Important Notifications">
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  aria-controls="notification-menu"
                  aria-haspopup="true"
                  onClick={handleOpenMenu}
                >
                  <Icon sx={iconsStyle}><NotificationsIcon /></Icon>
                </IconButton>
              </Tooltip>

              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? <Icon>menu_open</Icon> : <Icon>menu</Icon>}
                </Icon>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>

      {showAccountForm && (
        <TradeAccountForm
          userID={localStorage.getItem('userID')}
          onClose={() => setShowAccountForm(false)}
          tradeAccounts={tradeAccounts || []}
        />
      )}
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
  tradeAccounts: PropTypes.array.isRequired,
  selectedAccountID: PropTypes.string,
  setSelectedAccountID: PropTypes.func.isRequired,
};

export default DashboardNavbar;